// src/components/wells/WellDrainageNetworkTab.js
// FlowSense MOW+ — drawer tab for the Well Management Panel: the well's kind and register data,
// Well Intelligence score, ground elevation (TessaDEM) and the catch basins / drainage wells linked
// to it, each with its own inspection, clearance, work orders and elevation relation.
import React, { memo } from 'react';
import {
  Box, Flex, HStack, VStack, Text, Badge, SimpleGrid, Button, Tooltip, Alert, AlertIcon, useColorModeValue,
} from '@chakra-ui/react';
import { MdAutoAwesome, MdBuild } from 'react-icons/md';
import { INSPECTION_META, tierMeta, drainRoleMeta, wellKindLabel } from './wellIntelUi';
import GroundElevationCard from '../elevation/GroundElevationCard';

const Cell = ({ label, children, color }) => {
  const bg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const muted = useColorModeValue('gray.500', 'gray.400');
  return (
    <Box p={2} bg={bg} borderRadius="md">
      <Text fontSize="10px" color={muted} fontWeight="700" textTransform="uppercase">{label}</Text>
      <Text fontWeight="700" fontSize="sm" color={color}>{children}</Text>
    </Box>
  );
};

function WellDrainageNetworkTab({ well, intelWell, onOpenExplainer, onCreateWorkOrder }) {
  const card = useColorModeValue('gray.50', 'whiteAlpha.50');
  const muted = useColorModeValue('gray.500', 'gray.400');
  if (!well) return null;
  const w = intelWell || well;
  const intel = w.intel;
  const tm = tierMeta(intel?.tier);
  const insp = w.inspection;
  const im = INSPECTION_META[insp?.status] || INSPECTION_META.never_inspected;
  const nearby = w.drainage?.nearby || [];
  const register = Array.isArray(w.register) ? w.register : [];

  return (
    <VStack align="stretch" spacing={4}>
      <SimpleGrid columns={2} spacing={2}>
        <Cell label="Kind">{w.asset_type || wellKindLabel(w)}</Cell>
        <Cell label="Inspection"><Badge colorScheme={im.scheme}>{im.label}{insp?.overdue_days ? ` · ${insp.overdue_days}d` : ''}</Badge></Cell>
        <Cell label="Risk score" color={tm.color}>{intel?.score ?? '—'} <Badge ml={1} colorScheme={tm.scheme} fontSize="9px">{tm.label}</Badge></Cell>
        <Cell label="Next inspection due">{insp?.next_due_date || 'Schedule now'}</Cell>
        {register.map((f) => <Cell key={f.key} label={f.label}>{String(f.value)}</Cell>)}
      </SimpleGrid>

      {intel?.summary && (
        <HStack align="start" p={2.5} borderRadius="lg" bg={card} spacing={2}>
          <MdAutoAwesome color="#9F7AEA" style={{ marginTop: 2, flexShrink: 0 }} />
          <Text fontSize="sm">{intel.summary}</Text>
        </HStack>
      )}

      <GroundElevationCard assetId={well.id} compact />

      <Box>
        <Flex justify="space-between" align="center" mb={2}>
          <Text fontSize="xs" fontWeight="800" textTransform="uppercase" color={muted}>
            Linked catch basins & drainage ({nearby.length}) · {w.drainage?.radius_m ?? '—'} m
          </Text>
        </Flex>
        {!intelWell ? (
          <Alert status="info" borderRadius="lg" fontSize="sm"><AlertIcon />Drainage linkage loads with Well Intelligence…</Alert>
        ) : w.latitude == null ? (
          <Text fontSize="sm" color={muted}>Add coordinates to this well to link it to catch basins and drainage wells.</Text>
        ) : !nearby.length ? (
          <Text fontSize="sm" color="orange.400">No catch basin or drainage asset mapped within {w.drainage?.radius_m} m.</Text>
        ) : (
          <VStack align="stretch" spacing={1.5}>
            {nearby.map((d) => {
              const rm = drainRoleMeta(d.role);
              const di = d.inspection || {};
              const dm = INSPECTION_META[di.status] || INSPECTION_META.never_inspected;
              return (
                <Box key={String(d.id)} p={2} borderRadius="md" bg={card} borderLeftWidth="3px" borderLeftColor={d.impaired ? 'red.400' : `${rm.scheme}.400`}>
                  <Flex justify="space-between" gap={2}>
                    <HStack minW={0}><Badge fontSize="8px" colorScheme={rm.scheme}>{rm.label}</Badge><Text fontWeight="700" fontSize="sm" noOfLines={1}>{d.name || d.id}</Text></HStack>
                    <HStack spacing={2} flexShrink={0}>
                      <Text fontSize="xs" color={muted}>{d.distance_m} m</Text>
                      <Badge colorScheme={d.impaired ? 'red' : 'green'} fontSize="9px">{d.impaired ? d.reason : d.status || 'ok'}</Badge>
                    </HStack>
                  </Flex>
                  <HStack mt={1} spacing={2} fontSize="10px" color={muted} flexWrap="wrap">
                    <Badge fontSize="8px" colorScheme={dm.scheme} variant="subtle">{dm.label}{di.overdue_days ? ` · ${di.overdue_days}d` : ''}</Badge>
                    <Text>last {di.last_inspected_date || '—'}</Text>
                    {di.next_due_date && <Text>next {di.next_due_date}</Text>}
                    {di.clearance_score != null && <Text color={di.clearance_score < 50 ? 'red.400' : undefined}>clearance {di.clearance_score}%</Text>}
                    {di.last_cleaned_date && <Text>cleaned {di.last_cleaned_date}</Text>}
                    {d.open_work_orders > 0 && <Text color="purple.400">{d.open_work_orders} open WO</Text>}
                    {d.elevation_drop_m != null && (
                      <Tooltip hasArrow label="Well ground minus drainage ground (TessaDEM). Positive: run-off flows to it.">
                        <Text color={d.elevation_drop_m < 0 ? 'orange.400' : undefined}>{d.elevation_drop_m >= 0 ? `${d.elevation_drop_m} m downhill` : `${Math.abs(d.elevation_drop_m)} m uphill`}</Text>
                      </Tooltip>
                    )}
                  </HStack>
                </Box>
              );
            })}
          </VStack>
        )}
      </Box>

      <HStack>
        {onOpenExplainer && <Button size="sm" variant="outline" colorScheme="purple" leftIcon={<MdAutoAwesome />} onClick={() => onOpenExplainer(well.id)}>AI explainer</Button>}
        {onCreateWorkOrder && intelWell && (
          <Button size="sm" colorScheme="purple" leftIcon={<MdBuild />}
            onClick={() => onCreateWorkOrder(intelWell, (intel?.recommendations || []).find((r) => ['INSPECT_DRAINAGE', 'CLEAR_DRAINAGE'].includes(r.code)) || intel?.recommendations?.[0])}>
            Work order
          </Button>
        )}
      </HStack>
    </VStack>
  );
}

export default memo(WellDrainageNetworkTab);
